/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { uploadRecipeImage } from '../utils/uploadImage';
import '../styles/AddRecipe.css';
import { searchIngredients, CATEGORY_ORDER } from '../utils/ingredientUtils';


// Units list for the dropdown (kept here as UI-only config)
const UNITS = [
  { value: "tsp",      label: "tsp",      type: "volume"  },
  { value: "tbsp",     label: "tbsp",     type: "volume"  },
  { value: "fl oz",    label: "fl oz",    type: "volume"  },
  { value: "cup",      label: "cup",      type: "volume"  },
  { value: "scoop",    label: "scoop",    type: "volume"  },
  { value: "pt",       label: "pt",       type: "volume"  },
  { value: "qt",       label: "qt",       type: "volume"  },
  { value: "gal",      label: "gal",      type: "volume"  },
  { value: "ml",       label: "ml",       type: "volume"  },
  { value: "l",        label: "l",        type: "volume"  },
  { value: "oz",       label: "oz",       type: "weight"  },
  { value: "lb",       label: "lb",       type: "weight"  },
  { value: "g",        label: "g",        type: "weight"  },
  { value: "kg",       label: "kg",       type: "weight"  },
  { value: "whole",    label: "whole",    type: "count"   },
  { value: "slice",    label: "slice",    type: "count"   },
  { value: "clove",    label: "clove",    type: "count"   },
  { value: "sprig",    label: "sprig",    type: "count"   },
  { value: "stalk",    label: "stalk",    type: "count"   },
  { value: "can",      label: "can",      type: "count"   },
  { value: "box",      label: "box",      type: "count"   },
  { value: "pkg",      label: "pkg",      type: "count"   },
  { value: "stick",    label: "stick",    type: "count"   },
  { value: "packet",   label: "packet",   type: "count"   },
  { value: "envelope", label: "envelope", type: "count"   },
  { value: "pinch",    label: "pinch",    type: "special" },
  { value: "dash",     label: "dash",     type: "special" },
  { value: "to taste", label: "to taste", type: "special" },
  { value: "as needed",label: "as needed",type: "special" },
  { value: "heaping",  label: "heaping",  type: "special" },
  { value: "level",    label: "level",    type: "special" },
  { value: "rounded",  label: "rounded",  type: "special" },
  { value: "drop",     label: "drop",     type: "special" },
  { value: "handful",  label: "handful",  type: "special" },
  { value: "sheet",    label: "sheet",    type: "count"   },
];

// ─────────────────────────────────────────────────────────────────────────────
// STRUCTURED INGREDIENT ENTRY
// ─────────────────────────────────────────────────────────────────────────────

function decimalToFraction(decimal) {
  if (!decimal || decimal === 0) return '';
  const commonFractions = [[1,8],[1,4],[1,3],[1,2],[2,3],[3,4],[7,8]];
  const tolerance = 0.02;
  const whole = Math.floor(decimal);
  const remainder = decimal - whole;
  if (remainder < tolerance) return String(whole);
  if (remainder > 1 - tolerance) return String(whole + 1);
  let best = null, bestDiff = Infinity;
  for (const [n, d] of commonFractions) {
    const diff = Math.abs(remainder - n / d);
    if (diff < bestDiff) { bestDiff = diff; best = [n, d]; }
  }
  if (best && bestDiff < tolerance) {
    const frac = `${best[0]}/${best[1]}`;
    return whole === 0 ? frac : `${whole} ${frac}`;
  }
  return String(parseFloat(decimal.toFixed(2)));
}

const UNIT_PLURALS = {
  cup: 'cups', tsp: 'tsp', tbsp: 'tbsp', 'fl oz': 'fl oz',
  pt: 'pts', qt: 'qts', gal: 'gals', ml: 'ml', l: 'l',
  oz: 'oz', lb: 'lbs', g: 'g', kg: 'kg',
  whole: 'whole', slice: 'slices', clove: 'cloves',
  sprig: 'sprigs', stalk: 'stalks', can: 'cans', pkg: 'pkgs',
  pinch: 'pinches', dash: 'dashes', 'to taste': 'to taste', 'as needed': 'as needed',
};
function pluralizeUnit(unit, qty) {
  if (!unit) return unit;
  const q = parseFloat(qty);
  if (isNaN(q) || q <= 1) return unit;
  return UNIT_PLURALS[unit] || unit;
}

function buildOriginalString({ quantity, unit, name, prep }) {
  const parts = [];
  if (quantity) parts.push(decimalToFraction(parseFloat(quantity)));
  if (unit && unit !== 'whole' && unit !== 'as needed') parts.push(pluralizeUnit(unit, quantity));
  if (name) parts.push(name);
  if (prep) parts.push(prep);
  return parts.join(' ');
}

// ─────────────────────────────────────────────────────────────────────────────
// IMAGE UPLOAD BUTTON
// ─────────────────────────────────────────────────────────────────────────────
function ImageUploadButton({ uid, onUploaded, label = "Upload Image", style = {} }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadRecipeImage(file, uid);
      onUploaded(url);
    } catch (err) {
      console.error(err);
      alert('Image upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', ...style }}>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleChange} />
      <button type="button" className="small-button" disabled={uploading}
        onClick={() => inputRef.current?.click()} style={{ opacity: uploading ? 0.6 : 1 }}>
        {uploading ? 'Uploading…' : label}
      </button>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INGREDIENT AUTOCOMPLETE
// ─────────────────────────────────────────────────────────────────────────────
const INGREDIENT_CATEGORIES = [
  'Produce', 'Meat & Pork', 'Poultry', 'Seafood', 'Dairy & Eggs',
  'Bread & Bakery', 'Grains, Pasta & Rice', 'Canned & Jarred',
  'Oils, Sauces & Condiments', 'Herbs & Spices', 'Baking & Sweets', 'Other',
];

function IngredientAutocomplete({ value, onChange, onSelect, onSelectCustom, onAddToList }) {
  const { ingredients: dbIngredients } = useData();
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [pickedCategory, setPickedCategory] = useState('Produce');
  const [saving, setSaving] = useState(false);
  const wrapperRef = useRef(null);
  const suppressCloseRef = useRef(false);

  useEffect(() => {
    const found = searchIngredients(value, dbIngredients);
    setResults(found);
    setOpen(value.trim().length > 0);
    if (!value.trim()) { setShowCategoryPicker(false); setPickedCategory('Produce'); }
  }, [value, dbIngredients]);

  useEffect(() => {
    const handleClick = (e) => {
      if (suppressCloseRef.current) { suppressCloseRef.current = false; return; }
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false); setShowCategoryPicker(false); setPickedCategory('Produce');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const isExact = !!results.find(r => r.name.toLowerCase() === value.toLowerCase().trim());
  const trimmed = value.trim();

  const handleSaveToList = async () => {
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      await onAddToList({ name: trimmed, category: pickedCategory, aliases: [] });
      onSelect(trimmed);
      setOpen(false); setShowCategoryPicker(false); setPickedCategory('Produce');
    } catch (err) {
      console.error(err);
      alert('Failed to save ingredient. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', flex: 1 }}>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        onFocus={() => value.trim().length > 0 && setOpen(true)}
        placeholder="Ingredient name…" style={{ width: '100%', boxSizing: 'border-box' }} />

      {open && !showCategoryPicker && (
        <ul style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 500,
          background: 'white', border: '1px solid #ccc', borderTop: 'none',
          borderRadius: '0 0 6px 6px', margin: 0, padding: 0,
          listStyle: 'none', maxHeight: '260px', overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }}>
          {results.map((entry) => (
            <li key={entry.name}
              onMouseDown={(e) => { e.preventDefault(); onSelect(entry.name); setOpen(false); }}
              style={{ padding: '7px 12px', cursor: 'pointer', fontSize: '0.88rem', color: '#313131', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
              <span>{entry.name}</span>
              <span style={{ fontSize: '0.72rem', color: '#aaa' }}>{entry.category}</span>
            </li>
          ))}
          {trimmed.length > 0 && !isExact && (
            <>
              <li onMouseDown={(e) => { e.preventDefault(); onSelect(trimmed); if (onSelectCustom) onSelectCustom(trimmed); setOpen(false); }}
                style={{ padding: '7px 12px', cursor: 'pointer', fontSize: '0.88rem', color: '#E67E22', borderTop: '1px solid #eee' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#fff8f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
                + Use "{trimmed}" as custom ingredient
              </li>
              <li onMouseDown={(e) => { e.preventDefault(); suppressCloseRef.current = true; setOpen(false); setShowCategoryPicker(true); }}
                style={{ padding: '7px 12px', cursor: 'pointer', fontSize: '0.88rem', color: '#2196F3', borderTop: '1px solid #eee' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#EBF5FB'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
                + Add "{trimmed}" to ingredient list
              </li>
            </>
          )}
        </ul>
      )}

      {showCategoryPicker && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 500,
          background: '#EBF5FB', border: '1px solid #90CAF9', borderRadius: '0 0 8px 8px',
          padding: '10px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          display: 'flex', flexDirection: 'column', gap: '8px',
        }}>
          <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1565C0' }}>Add "{trimmed}" to ingredient list</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '0.75rem', color: '#555', fontWeight: 600, whiteSpace: 'nowrap' }}>Category:</label>
            <select value={pickedCategory} onChange={(e) => setPickedCategory(e.target.value)}
              style={{ flex: 1, fontSize: '0.82rem', padding: '4px 6px', border: '1px solid #90CAF9', borderRadius: '4px' }}>
              {INGREDIENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button type="button" onClick={handleSaveToList} disabled={saving}
              style={{ flex: 1, padding: '5px 0', fontSize: '0.82rem', fontWeight: 700, background: saving ? '#90CAF9' : '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: saving ? 'default' : 'pointer' }}>
              {saving ? 'Saving…' : 'Save to List'}
            </button>
            <button type="button" onClick={() => { setShowCategoryPicker(false); setPickedCategory('Produce'); }}
              style={{ padding: '5px 12px', fontSize: '0.82rem', background: 'white', color: '#666', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const BLANK_FIELDS = { quantity: '', unit: '', name: '', prep: '', original: '', overrideOriginal: false };

function StructuredIngredientInput({ onAdd, editingValue, isEditing, onCancelEdit, onSelectCustom, onAddToList }) {
  const [fields, setFields] = useState(BLANK_FIELDS);

  useEffect(() => {
    if (editingValue) setFields({ ...BLANK_FIELDS, ...editingValue });
    else setFields(BLANK_FIELDS);
  }, [editingValue]);

  useEffect(() => {
    if (!fields.overrideOriginal) {
      setFields(prev => ({ ...prev, original: buildOriginalString(prev) }));
    }
  }, [fields.quantity, fields.unit, fields.name, fields.prep, fields.overrideOriginal]);

  const set = (key, val) => setFields(prev => ({ ...prev, [key]: val }));

  const handleSubmit = () => {
    if (!fields.name.trim()) return;
    onAdd({ ...fields, original: fields.original || buildOriginalString(fields) });
    setFields(BLANK_FIELDS);
  };

  const unitGroups = [
    { label: 'Volume', options: UNITS.filter(u => u.type === 'volume') },
    { label: 'Weight', options: UNITS.filter(u => u.type === 'weight') },
    { label: 'Count',  options: UNITS.filter(u => u.type === 'count')  },
    { label: 'Special',options: UNITS.filter(u => u.type === 'special')},
  ];

  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '12px', background: '#fafafa', marginTop: '8px' }}>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '8px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '80px' }}>
          <label style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>Qty</label>
          <input type="number" min="0" step="0.25" value={fields.quantity}
            onChange={(e) => set('quantity', e.target.value)} placeholder="e.g. 1.5" style={{ width: '100%' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '110px' }}>
          <label style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>Unit</label>
          <select value={fields.unit} onChange={(e) => set('unit', e.target.value)} style={{ width: '100%' }}>
            <option value="">— none —</option>
            {unitGroups.map(group => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
              </optgroup>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: '1 1 180px', minWidth: '180px' }}>
          <label style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>Ingredient name</label>
          <IngredientAutocomplete value={fields.name} onChange={(val) => set('name', val)}
            onSelect={(val) => set('name', val)} onSelectCustom={onSelectCustom} onAddToList={onAddToList} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: '1 1 130px', minWidth: '130px' }}>
          <label style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>Prep (optional)</label>
          <input type="text" value={fields.prep} onChange={(e) => set('prep', e.target.value)}
            placeholder="e.g. diced, sifted" style={{ width: '100%', boxSizing: 'border-box' }} />
        </div>
      </div>

      <div style={{ marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <label style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>Original string</label>
          <label style={{ fontSize: '0.72rem', color: '#E67E22', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input type="checkbox" checked={fields.overrideOriginal} onChange={(e) => set('overrideOriginal', e.target.checked)} />
            Override
          </label>
        </div>
        <input type="text" value={fields.original} onChange={(e) => set('original', e.target.value)}
          disabled={!fields.overrideOriginal}
          style={{ width: '100%', boxSizing: 'border-box', background: fields.overrideOriginal ? 'white' : '#f0f0f0', color: fields.overrideOriginal ? '#313131' : '#888', fontStyle: fields.overrideOriginal ? 'normal' : 'italic', border: '1px solid #ddd', borderRadius: '4px', padding: '5px 8px', fontSize: '0.88rem' }} />
        {!fields.overrideOriginal && (
          <p style={{ margin: '3px 0 0', fontSize: '0.72rem', color: '#aaa' }}>
            Auto-generated from fields above. Check "Override" to edit manually.
          </p>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button className="add-button" onClick={handleSubmit} disabled={!fields.name.trim()} style={{ opacity: fields.name.trim() ? 1 : 0.5 }}>
          {isEditing ? 'Update Ingredient' : 'Add Ingredient'}
        </button>
        {isEditing && onCancelEdit && (
          <button className="edit-button" onClick={() => { setFields(BLANK_FIELDS); onCancelEdit(); }} style={{ marginLeft: '4px' }}>
            Cancel Edit
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function AddRecipe() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user }  = useAuth();

  const { recipes, saveRecipe, deleteRecipe, saveIngredient } = useData();

  const nextRecipeId = () => {
    if (!recipes || recipes.length === 0) return "";
    return Math.max(...recipes.map(r => Number(r.id))) + 1;
  };

  const blankRecipe = () => ({
    id: nextRecipeId(),
    title: "",
    image: "",
    readyInMinutes: "",
    servings: "",
    veryHealthy: false,
    cuisines: [],
    dishTypes: [],
    occasions: [],
    extendedIngredients: [],
    analyzedInstructions: [],
    equipment: []
  });

  const [recipe, setRecipe] = useState(blankRecipe());

  // ── Dirty tracking — true when form has unsaved changes ───────────────────
  const isDirtyRef = useRef(false);
  const markDirty  = () => { isDirtyRef.current = true; };
  const markClean  = () => { isDirtyRef.current = false; };

  // Wrap setRecipe to auto-mark dirty on any recipe change
  const setRecipeDirty = (val) => {
    markDirty();
    setRecipe(val);
  };

  // ── Save notification state ───────────────────────────────────────────────
  const [saveNotification, setSaveNotification] = useState(''); // '' | 'saving' | 'saved' | 'error'
  const [savedRecipeTitle, setSavedRecipeTitle] = useState('');

  // ── Confirm unsaved changes before a destructive action ───────────────────
  const confirmIfDirty = (message) => {
    if (!isDirtyRef.current) return true;
    return window.confirm(message);
  };

  // Ingredient section inputs
  const [currentIngredientSection, setCurrentIngredientSection]         = useState("");
  const [activeIngredientSectionIndex, setActiveIngredientSectionIndex] = useState(null);
  const [editingIngredient, setEditingIngredient]                       = useState(null);

  // Instruction section inputs
  const [currentInstructionSection, setCurrentInstructionSection]         = useState("");
  const [currentSectionNote, setCurrentSectionNote]                       = useState("");
  const [currentSectionStep, setCurrentSectionStep]                       = useState("");
  const [activeInstructionSectionIndex, setActiveInstructionSectionIndex] = useState(null);
  const [editingSectionStep, setEditingSectionStep]                       = useState(null);

  // Step image inputs
  const [currentStepImageAlt, setCurrentStepImageAlt]         = useState("");
  const [pendingStepImageUrl, setPendingStepImageUrl]         = useState("");
  const [selectedSectionStepIndex, setSelectedSectionStepIndex] = useState(null);

  // Tag inputs
  const [currentCuisine, setCurrentCuisine]   = useState("");
  const [currentDishType, setCurrentDishType] = useState("");
  const [currentOccasion, setCurrentOccasion] = useState("");

  // Equipment inputs
  const [currentEquipmentItems, setCurrentEquipmentItems]       = useState("");
  const [currentEquipmentImageAlt, setCurrentEquipmentImageAlt] = useState("");
  const [pendingEquipmentImageUrl, setPendingEquipmentImageUrl] = useState("");

  // UI state
  const [generatedJSON, setGeneratedJSON]       = useState("");
  const [showJSON, setShowJSON]                 = useState(false);
  const [showLoadRecipe, setShowLoadRecipe]     = useState(false);
  const [loadRecipeSearch, setLoadRecipeSearch] = useState('');
  const [customIngredients, setCustomIngredients] = useState([]);

  // Main recipe image upload state
  const [mainImageUploading, setMainImageUploading] = useState(false);
  const mainImageInputRef = useRef(null);
  const importJsonRef     = useRef(null);

  // ── Auto-set ID once recipes load from Firebase ───────────────────────────
  useEffect(() => {
    if (recipes.length > 0 && recipe.id === "") {
      const maxId = Math.max(...recipes.map(r => Number(r.id)));
      setRecipe(prev => ({ ...prev, id: maxId + 1 }));
    }
  }, [recipes]);

  // ── Browser unload warning ─────────────────────────────────────────────────
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // ── In-app navigation warning (clicking nav links) ─────────────────────────
  useEffect(() => {
    const handleClick = (e) => {
      const link = e.target.closest('a');
      if (!link || !isDirtyRef.current) return;
      const href = link.getAttribute('href');
      if (href && href.startsWith('/') && !href.startsWith('/add-recipe')) {
        e.preventDefault();
        if (window.confirm('You are leaving the Add/Edit Recipe page. Are you sure?\n\nYour changes will be lost if you don\'t save first.')) {
          markClean();
          navigate(href);
        }
      }
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('mode') === 'edit') {
      setShowLoadRecipe(true);
      window.history.replaceState({}, '', '/add-recipe');
    }
  }, [location.search]);

  // ── Reset helpers ──────────────────────────────────────────────────────────
  const resetForm = (newRecipe) => {
    setRecipe(newRecipe);
    markClean();
    setGeneratedJSON(""); setShowJSON(false);
    setActiveIngredientSectionIndex(null); setActiveInstructionSectionIndex(null);
    setEditingIngredient(null); setEditingSectionStep(null);
    setSaveNotification('');
  };

  // ── Load / New ─────────────────────────────────────────────────────────────
  const handleLoadRecipe = (recipeToLoad) => {
    const toArr = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      return Object.values(val);
    };

    const normalizedInstructions = toArr(recipeToLoad.analyzedInstructions).map(section => ({
      ...section,
      steps: toArr(section.steps).map(step => ({ ...step, images: toArr(step.images) })),
    }));
    const normalizedIngredients = toArr(recipeToLoad.extendedIngredients).map(section => ({
      ...section, ingredients: toArr(section.ingredients),
    }));
    const normalizedEquipment = toArr(recipeToLoad.equipment).map(eq => ({ ...eq, images: toArr(eq.images) }));

    resetForm({
      ...recipeToLoad,
      cuisines:             toArr(recipeToLoad.cuisines),
      dishTypes:            toArr(recipeToLoad.dishTypes),
      occasions:            toArr(recipeToLoad.occasions),
      equipment:            normalizedEquipment,
      extendedIngredients:  normalizedIngredients,
      analyzedInstructions: normalizedInstructions,
    });
    setShowLoadRecipe(false);
    setLoadRecipeSearch('');
  };

  const handleNewRecipe = () => {
    if (!confirmIfDirty('Are you sure? Any unsaved changes will be lost.')) return;
    resetForm(blankRecipe());
  };

  // ── Import JSON ────────────────────────────────────────────────────────────
  const handleImportJSON = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    if (!confirmIfDirty('You have unsaved changes. Importing will replace the current recipe. Continue?')) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const parsed = JSON.parse(evt.target.result);
        const maxId  = recipes.length > 0 ? Math.max(...recipes.map(r => Number(r.id))) : 0;
        const newId  = maxId + 1;

        const toArr = (val) => {
          if (!val) return [];
          if (Array.isArray(val)) return val;
          return Object.values(val);
        };

        resetForm({
          ...parsed,
          id:                   newId,
          cuisines:             toArr(parsed.cuisines),
          dishTypes:            toArr(parsed.dishTypes),
          occasions:            toArr(parsed.occasions),
          equipment:            toArr(parsed.equipment).map(eq => ({ ...eq, images: toArr(eq.images) })),
          extendedIngredients:  toArr(parsed.extendedIngredients).map(s => ({ ...s, ingredients: toArr(s.ingredients) })),
          analyzedInstructions: toArr(parsed.analyzedInstructions).map(s => ({ ...s, steps: toArr(s.steps).map(st => ({ ...st, images: toArr(st.images) })) })),
        });

        // Mark dirty after import so user is warned if they navigate away without saving
        markDirty();

        alert(`"${parsed.title || 'Recipe'}" imported! Assigned new ID #${newId}. Review and click Save to Firebase when ready.`);
      } catch (err) {
        console.error(err);
        alert('Failed to import — make sure the file is a valid recipe JSON.');
      }
    };
    reader.readAsText(file);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // INGREDIENT SECTION HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════

  const handleAddIngredientSection = () => {
    if (!currentIngredientSection.trim()) return;
    const updated = [...recipe.extendedIngredients, { section: currentIngredientSection.trim(), ingredients: [] }];
    setRecipeDirty({ ...recipe, extendedIngredients: updated });
    setActiveIngredientSectionIndex(updated.length - 1);
    setCurrentIngredientSection("");
  };

  const handleRemoveIngredientSection = (sIndex) => {
    setRecipeDirty({ ...recipe, extendedIngredients: recipe.extendedIngredients.filter((_, i) => i !== sIndex) });
    if (activeIngredientSectionIndex === sIndex) setActiveIngredientSectionIndex(null);
  };

  const handleAddIngredientToSection = (sIndex, fields) => {
    const updated  = [...recipe.extendedIngredients];
    const section  = { ...updated[sIndex], ingredients: [...updated[sIndex].ingredients] };
    const newIngredient = {
      id:       section.ingredients.length + 1,
      original: fields.original,
      quantity: fields.quantity ? parseFloat(fields.quantity) : null,
      unit:     fields.unit     || null,
      name:     fields.name     || null,
      prep:     fields.prep     || null,
    };
    if (editingIngredient?.sectionIndex === sIndex && editingIngredient?.itemIndex != null) {
      section.ingredients[editingIngredient.itemIndex] = { ...newIngredient, id: editingIngredient.itemIndex + 1 };
      setEditingIngredient(null);
    } else {
      newIngredient.id = section.ingredients.length + 1;
      section.ingredients.push(newIngredient);
    }
    updated[sIndex] = section;
    setRecipeDirty({ ...recipe, extendedIngredients: updated });
  };

  const handleRemoveIngredientFromSection = (sIndex, ingIndex) => {
    const updated = [...recipe.extendedIngredients];
    updated[sIndex] = {
      ...updated[sIndex],
      ingredients: updated[sIndex].ingredients.filter((_, i) => i !== ingIndex).map((ing, i) => ({ ...ing, id: i + 1 })),
    };
    setRecipeDirty({ ...recipe, extendedIngredients: updated });
  };

  const handleEditIngredient = (sIndex, ingIndex) => {
    const ing = recipe.extendedIngredients[sIndex].ingredients[ingIndex];
    setEditingIngredient({
      sectionIndex: sIndex, itemIndex: ingIndex,
      fields: {
        quantity: ing.quantity != null ? String(ing.quantity) : '',
        unit: ing.unit || '', name: ing.name || ing.original || '',
        prep: ing.prep || '', original: ing.original || '', overrideOriginal: false,
      },
    });
    setActiveIngredientSectionIndex(sIndex);
    setTimeout(() => {
      document.getElementById(`ingredient-form-${sIndex}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // INSTRUCTION SECTION HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════

  const handleAddInstructionSection = () => {
    if (!currentInstructionSection.trim()) return;
    const updated = [...recipe.analyzedInstructions, {
      section: currentInstructionSection.trim(), note: currentSectionNote.trim(), steps: []
    }];
    setRecipeDirty({ ...recipe, analyzedInstructions: updated });
    setActiveInstructionSectionIndex(updated.length - 1);
    setCurrentInstructionSection(""); setCurrentSectionNote("");
  };

  const handleRemoveInstructionSection = (sIndex) => {
    setRecipeDirty({ ...recipe, analyzedInstructions: recipe.analyzedInstructions.filter((_, i) => i !== sIndex) });
    if (activeInstructionSectionIndex === sIndex) setActiveInstructionSectionIndex(null);
  };

  const handleUpdateSectionNote = (sIndex, note) => {
    const updated = [...recipe.analyzedInstructions];
    updated[sIndex] = { ...updated[sIndex], note };
    setRecipeDirty({ ...recipe, analyzedInstructions: updated });
  };

  const handleAddStepToSection = (sIndex) => {
    if (!currentSectionStep.trim()) return;
    const updated = [...recipe.analyzedInstructions];
    const section = { ...updated[sIndex], steps: [...updated[sIndex].steps] };
    if (editingSectionStep?.sectionIndex === sIndex && editingSectionStep?.isInsert) {
      section.steps.splice(editingSectionStep.stepIndex, 0, { number: editingSectionStep.stepIndex + 1, step: currentSectionStep.trim(), images: [] });
      section.steps = section.steps.map((s, i) => ({ ...s, number: i + 1 }));
      setEditingSectionStep(null);
    } else if (editingSectionStep?.sectionIndex === sIndex && !editingSectionStep?.isInsert) {
      section.steps[editingSectionStep.stepIndex] = { ...section.steps[editingSectionStep.stepIndex], step: currentSectionStep.trim() };
      setEditingSectionStep(null);
    } else {
      section.steps.push({ number: section.steps.length + 1, step: currentSectionStep.trim(), images: [] });
    }
    updated[sIndex] = section;
    setRecipeDirty({ ...recipe, analyzedInstructions: updated });
    setCurrentSectionStep('');
  };

  const handleRemoveStepFromSection = (sIndex, stepIndex) => {
    const updated = [...recipe.analyzedInstructions];
    updated[sIndex] = {
      ...updated[sIndex],
      steps: updated[sIndex].steps.filter((_, i) => i !== stepIndex).map((s, i) => ({ ...s, number: i + 1 }))
    };
    setRecipeDirty({ ...recipe, analyzedInstructions: updated });
  };

  const handleAddSectionStepImage = (sIndex, stepIndex) => {
    if (!pendingStepImageUrl || !currentStepImageAlt.trim()) return;
    const updated = [...recipe.analyzedInstructions];
    updated[sIndex].steps[stepIndex].images.push({ url: pendingStepImageUrl, alt: currentStepImageAlt.trim() });
    setRecipeDirty({ ...recipe, analyzedInstructions: updated });
    setPendingStepImageUrl(""); setCurrentStepImageAlt(""); setSelectedSectionStepIndex(null);
  };

  const handleRemoveSectionStepImage = (sIndex, stepIndex, imgIndex) => {
    const updated = [...recipe.analyzedInstructions];
    updated[sIndex].steps[stepIndex].images = updated[sIndex].steps[stepIndex].images.filter((_, i) => i !== imgIndex);
    setRecipeDirty({ ...recipe, analyzedInstructions: updated });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // TAG HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════

  const handleAddCuisine = () => {
    if (currentCuisine.trim() && !recipe.cuisines.includes(currentCuisine.trim())) {
      setRecipeDirty({ ...recipe, cuisines: [...recipe.cuisines, currentCuisine.trim()] });
      setCurrentCuisine("");
    }
  };
  const handleRemoveCuisine  = (c) => setRecipeDirty({ ...recipe, cuisines:  recipe.cuisines.filter(x => x !== c) });

  const handleAddDishType = () => {
    if (currentDishType.trim() && !recipe.dishTypes.includes(currentDishType.trim())) {
      setRecipeDirty({ ...recipe, dishTypes: [...recipe.dishTypes, currentDishType.trim()] });
      setCurrentDishType("");
    }
  };
  const handleRemoveDishType = (d) => setRecipeDirty({ ...recipe, dishTypes: recipe.dishTypes.filter(x => x !== d) });

  const handleAddOccasion = () => {
    if (currentOccasion.trim() && !recipe.occasions.includes(currentOccasion.trim())) {
      setRecipeDirty({ ...recipe, occasions: [...recipe.occasions, currentOccasion.trim()] });
      setCurrentOccasion("");
    }
  };
  const handleRemoveOccasion = (o) => setRecipeDirty({ ...recipe, occasions: recipe.occasions.filter(x => x !== o) });

  // ═══════════════════════════════════════════════════════════════════════════
  // EQUIPMENT HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════

  const handleAddEquipment = () => {
    if (!currentEquipmentItems.trim()) return;
    setRecipeDirty({ ...recipe, equipment: [...recipe.equipment, { items: currentEquipmentItems.trim(), images: [] }] });
    setCurrentEquipmentItems("");
  };
  const handleRemoveEquipment = (index) =>
    setRecipeDirty({ ...recipe, equipment: recipe.equipment.filter((_, i) => i !== index) });

  const handleAddEquipmentImage = (equipIndex) => {
    if (!pendingEquipmentImageUrl || !currentEquipmentImageAlt.trim()) return;
    const updated = [...recipe.equipment];
    updated[equipIndex].images.push({ url: pendingEquipmentImageUrl, alt: currentEquipmentImageAlt.trim() });
    setRecipeDirty({ ...recipe, equipment: updated });
    setPendingEquipmentImageUrl(""); setCurrentEquipmentImageAlt("");
  };
  const handleRemoveEquipmentImage = (equipIndex, imgIndex) => {
    const updated = [...recipe.equipment];
    updated[equipIndex].images = updated[equipIndex].images.filter((_, i) => i !== imgIndex);
    setRecipeDirty({ ...recipe, equipment: updated });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CUSTOM INGREDIENT TRACKING
  // ═══════════════════════════════════════════════════════════════════════════

  const handleSelectCustom = (name) => {
    setCustomIngredients(prev => {
      if (prev.find(c => c.name.toLowerCase() === name.toLowerCase())) return prev;
      return [...prev, { name, category: 'Other', aliases: '' }];
    });
  };
  const updateCustomIngredient = (index, key, value) =>
    setCustomIngredients(prev => prev.map((c, i) => i === index ? { ...c, [key]: value } : c));
  const removeCustomIngredient = (index) =>
    setCustomIngredients(prev => prev.filter((_, i) => i !== index));

  // ═══════════════════════════════════════════════════════════════════════════
  // SAVE / DELETE / JSON
  // ═══════════════════════════════════════════════════════════════════════════

  const generateJSON = () => { setGeneratedJSON(JSON.stringify(recipe, null, 4)); setShowJSON(true); };

  const handleSaveToFirebase = async () => {
    if (!recipe.id || !recipe.title) {
      alert('Recipe must have an ID and a title before saving.');
      return;
    }
    setSaveNotification('saving');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    try {
      const toSave = {
        ...recipe,
        id: isNaN(recipe.id) ? recipe.id : Number(recipe.id),
        readyInMinutes: Number(recipe.readyInMinutes) || 0,
        servings: Number(recipe.servings) || 1,
      };
      await saveRecipe(toSave);
      setSavedRecipeTitle(recipe.title);
      setSaveNotification('saved');
      markClean();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => setSaveNotification(''), 4000);
    } catch (err) {
      console.error(err);
      setSaveNotification('error');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => setSaveNotification(''), 4000);
    }
  };

  const handleDeleteFromFirebase = async () => {
    if (!recipe.id) return;
    if (!window.confirm('Delete "' + recipe.title + '" from Firebase? This cannot be undone.')) return;
    try {
      await deleteRecipe(recipe.id);
      resetForm(blankRecipe());
      alert('Recipe deleted.');
    } catch (err) {
      console.error(err);
      alert('Error deleting recipe.');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedJSON);
    alert("JSON copied to clipboard!");
  };

  const downloadJSON = () => {
    const blob = new Blob([generatedJSON], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `recipe-${recipe.id || 'new'}.json`; link.click();
    URL.revokeObjectURL(url);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER HELPER — step list
  // ═══════════════════════════════════════════════════════════════════════════

  const renderStepItems = ({ steps, sIndex }) => (
    <ul className="item-list">
      {steps.map((step, stepIndex) => {
        const imgKey = `sec-${sIndex}-${stepIndex}`;
        const isBeingEdited = editingSectionStep?.sectionIndex === sIndex && editingSectionStep?.stepIndex === stepIndex;
        return (
          <li key={stepIndex} className="step-item" style={{
            background: isBeingEdited ? '#fff8e1' : undefined,
            border: isBeingEdited ? '1px solid #FFD54F' : undefined,
            borderRadius: isBeingEdited ? '4px' : undefined,
          }}>
            <div className="step-content">
              <div className="step-text"><strong>Step {step.number}:</strong> {step.step}</div>
              <div className="item-buttons">
                <button className="edit-button" title={`Insert a new step before Step ${step.number}`}
                  onClick={() => {
                    setEditingSectionStep({ sectionIndex: sIndex, stepIndex, isInsert: true });
                    setActiveInstructionSectionIndex(sIndex);
                    setCurrentSectionStep('');
                    setTimeout(() => {
                      document.getElementById(`step-form-${sIndex}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }, 50);
                  }}>Insert Before</button>
                <button className="edit-button" onClick={() => {
                  setCurrentSectionStep(step.step);
                  setEditingSectionStep({ sectionIndex: sIndex, stepIndex, isInsert: false, originalStep: step });
                  setActiveInstructionSectionIndex(sIndex);
                }}>Edit</button>
                <button className="remove-button" onClick={() => handleRemoveStepFromSection(sIndex, stepIndex)}>Remove</button>
              </div>
            </div>

            <div className="image-subsection">
              <h5>Images for this step:</h5>
              {step.images.length > 0 && (
                <div className="image-list">
                  {step.images.map((image, imgIndex) => (
                    <div key={imgIndex} className="image-item">
                      <img src={image.url} alt={image.alt} style={{ width: '60px', height: '45px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: '0.82rem', color: '#555' }}>{image.alt}</span>
                      <button className="small-remove-button" onClick={() => handleRemoveSectionStepImage(sIndex, stepIndex, imgIndex)}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {selectedSectionStepIndex === imgKey ? (
                <div className="image-input-group" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <ImageUploadButton uid={user?.uid} label={pendingStepImageUrl ? '✓ Uploaded — Replace' : 'Upload Image'} onUploaded={(url) => setPendingStepImageUrl(url)} />
                    {pendingStepImageUrl && (
                      <img src={pendingStepImageUrl} alt="preview" style={{ width: '50px', height: '38px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }} />
                    )}
                  </div>
                  <input type="text" value={currentStepImageAlt} onChange={(e) => setCurrentStepImageAlt(e.target.value)}
                    placeholder="Image description (alt text)" style={{ width: '100%', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button className="small-button" disabled={!pendingStepImageUrl || !currentStepImageAlt.trim()}
                      style={{ opacity: (!pendingStepImageUrl || !currentStepImageAlt.trim()) ? 0.5 : 1 }}
                      onClick={() => handleAddSectionStepImage(sIndex, stepIndex)}>Add Image</button>
                    <button className="small-button" onClick={() => { setSelectedSectionStepIndex(null); setPendingStepImageUrl(""); setCurrentStepImageAlt(""); }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button className="small-button" onClick={() => { setSelectedSectionStepIndex(imgKey); setPendingStepImageUrl(""); setCurrentStepImageAlt(""); }}>
                  + Add Image to Step
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
      <div className="add-recipe-container">
        <div className="add-recipe-header">
          <h1>Add/Edit Recipe</h1>
          <div className="button-group">
            <button onClick={() => {
              if (!confirmIfDirty('You have unsaved changes. Loading a new recipe will discard them. Continue?')) return;
              setShowLoadRecipe(true);
            }} className="action-button load-button">Load Recipe</button>
            <button onClick={handleNewRecipe} className="action-button new-button">New Recipe</button>
            <input ref={importJsonRef} type="file" accept=".json,application/json" style={{ display: 'none' }} onChange={handleImportJSON} />
            <button className="action-button" style={{ background: 'linear-gradient(35deg, #8E44AD, #6C3483)' }}
              onClick={() => importJsonRef.current?.click()}>
              Import JSON
            </button>
          </div>
        </div>

        {/* ── Save notification banner ── */}
        {saveNotification === 'saved' && (
          <div style={{
            margin: '0 0 1rem', padding: '10px 16px',
            background: '#F0FBF0', border: '1px solid #4CAF50',
            borderRadius: '8px', fontSize: '0.92rem', color: '#2E7D32',
            display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <span style={{ fontSize: '1.1rem' }}>✅</span>
            <span><strong>"{savedRecipeTitle}"</strong> has been saved to Firebase successfully.</span>
          </div>
        )}
        {saveNotification === 'saving' && (
          <div style={{
            margin: '0 0 1rem', padding: '10px 16px',
            background: '#EBF5FB', border: '1px solid #90CAF9',
            borderRadius: '8px', fontSize: '0.92rem', color: '#1565C0',
            display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <span>⏳</span><span>Saving to Firebase…</span>
          </div>
        )}
        {saveNotification === 'error' && (
          <div style={{
            margin: '0 0 1rem', padding: '10px 16px',
            background: '#fff0f0', border: '1px solid #ffcdd2',
            borderRadius: '8px', fontSize: '0.92rem', color: '#c62828',
            display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <span>❌</span><span>Error saving recipe. Please try again.</span>
          </div>
        )}

        {/* ── Load Recipe Modal ── */}
        {showLoadRecipe && (
          <div className="modal" onClick={() => { setShowLoadRecipe(false); setLoadRecipeSearch(''); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Select Recipe to Edit</h2>
                <button className="close-button" onClick={() => { setShowLoadRecipe(false); setLoadRecipeSearch(''); }}>×</button>
              </div>
              <div style={{ padding: '0 1rem 0.75rem' }}>
                <input type="text" autoFocus value={loadRecipeSearch}
                  onChange={(e) => setLoadRecipeSearch(e.target.value)}
                  placeholder="Search recipes…" style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div className="recipe-list">
                {recipes.slice().sort((a, b) => a.title.localeCompare(b.title))
                  .filter(r => r.title.toLowerCase().includes(loadRecipeSearch.toLowerCase().trim()))
                  .map((r) => (
                    <div key={r.id} className="recipe-list-item" onClick={() => handleLoadRecipe(r)}>
                      <span className="recipe-id">#{r.id}</span>
                      <span className="recipe-title">{r.title}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Basic Information ── */}
        <div className="section">
          <h3>Basic Information</h3>
          <div className="form-group">
            <label>Recipe ID (must be unique):</label>
            <input type="number" value={recipe.id}
              onChange={(e) => setRecipeDirty({ ...recipe, id: Number(e.target.value) })} placeholder="e.g., 100" />
          </div>
          <div className="form-group">
            <label>Recipe Title:</label>
            <input type="text" value={recipe.title}
              onChange={(e) => setRecipeDirty({ ...recipe, title: e.target.value })} placeholder="e.g., Texas Chili" />
          </div>

          <div className="form-group">
            <label>Recipe Image:</label>
            {recipe.image && (
              <div style={{ marginBottom: '8px' }}>
                <img src={recipe.image} alt="Recipe preview"
                  style={{ width: '140px', height: '105px', objectFit: 'cover', borderRadius: '6px', display: 'block', border: '1px solid #ddd' }} />
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <input ref={mainImageInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  setMainImageUploading(true);
                  try {
                    const url = await uploadRecipeImage(file, user?.uid);
                    setRecipeDirty({ ...recipe, image: url });
                  } catch (err) {
                    console.error(err);
                    alert('Image upload failed. Please try again.');
                  } finally {
                    setMainImageUploading(false);
                    if (mainImageInputRef.current) mainImageInputRef.current.value = '';
                  }
                }} />
              <button type="button" className="add-button" disabled={mainImageUploading}
                onClick={() => mainImageInputRef.current?.click()} style={{ opacity: mainImageUploading ? 0.6 : 1 }}>
                {mainImageUploading ? 'Uploading…' : recipe.image ? 'Replace Image' : 'Upload Image'}
              </button>
              {recipe.image && (
                <button type="button" className="remove-button"
                  onClick={() => setRecipeDirty({ ...recipe, image: '' })}
                  style={{ padding: '6px 12px', fontSize: '0.85rem' }}>Remove Image</button>
              )}
            </div>
            {recipe.image && (
              <p style={{ margin: '6px 0 0', fontSize: '0.72rem', color: '#aaa', wordBreak: 'break-all' }}>{recipe.image}</p>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Ready in Minutes:</label>
              <input type="number" value={recipe.readyInMinutes}
                onChange={(e) => setRecipeDirty({ ...recipe, readyInMinutes: Number(e.target.value) })} placeholder="e.g., 95" />
            </div>
            <div className="form-group">
              <label>Servings:</label>
              <input type="number" value={recipe.servings}
                onChange={(e) => setRecipeDirty({ ...recipe, servings: Number(e.target.value) })} placeholder="e.g., 4" />
            </div>
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input type="checkbox" checked={recipe.veryHealthy}
                onChange={(e) => setRecipeDirty({ ...recipe, veryHealthy: e.target.checked })} />
              Very Healthy (Used to include the recipe under the "Healthy" filter at the top of the app, along with its category)
            </label>
          </div>
        </div>

        {/* ── Cuisines ── */}
        <div className="section">
          <h3>Category</h3>
          <div className="add-item-group">
            <input type="text" value={currentCuisine} onChange={(e) => setCurrentCuisine(e.target.value)}
              placeholder="e.g., Main Course, Side Dish, Sauce, Dessert, Bread, Breakfast, Drink"
              onKeyPress={(e) => e.key === 'Enter' && handleAddCuisine()} />
            <button className="add-button" onClick={handleAddCuisine}>Add Category</button>
          </div>
          Used to match the category buttons at the top of the recipe app. Multiple categories are allowed.
          <div className="tag-list">
            {recipe.cuisines.map((c, i) => (
              <span key={i} className="tag">{c}<button className="tag-remove" onClick={() => handleRemoveCuisine(c)}>×</button></span>
            ))}
          </div>
        </div>

        {/* ── Dish Types ── */}
        <div className="section">
          <h3>Dish Types</h3>
          <div className="add-item-group">
            <input type="text" value={currentDishType} onChange={(e) => setCurrentDishType(e.target.value)}
              placeholder="e.g., Snack, Shake, American, Chinese, Mexican, Italian, ..."
              onKeyPress={(e) => e.key === 'Enter' && handleAddDishType()} />
            <button className="add-button" onClick={handleAddDishType}>Add Dish Type</button>
          </div>
          This information is used to tag the recipe and make it searchable in the search bar. Multiple types are allowed.
          <div className="tag-list">
            {recipe.dishTypes.map((d, i) => (
              <span key={i} className="tag">{d}<button className="tag-remove" onClick={() => handleRemoveDishType(d)}>×</button></span>
            ))}
          </div>
        </div>

        {/* ── Occasions ── */}
        <div className="section">
          <h3>Occasions</h3>
          <div className="add-item-group">
            <input type="text" value={currentOccasion} onChange={(e) => setCurrentOccasion(e.target.value)}
              placeholder="e.g., Christmas, Thanksgiving, Birthday"
              onKeyPress={(e) => e.key === 'Enter' && handleAddOccasion()} />
            <button className="add-button" onClick={handleAddOccasion}>Add Occasion</button>
          </div>
          <div className="tag-list">
            {recipe.occasions.map((o, i) => (
              <span key={i} className="tag">{o}<button className="tag-remove" onClick={() => handleRemoveOccasion(o)}>×</button></span>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            INGREDIENTS
        ══════════════════════════════════════════════════════════════════ */}
        <div className="section">
          <h3>Ingredients</h3>
          <div className="add-item-group" style={{ marginBottom: '1rem' }}>
            <input type="text" value={currentIngredientSection}
              onChange={(e) => setCurrentIngredientSection(e.target.value)}
              placeholder="Section name, e.g. Batter, Topping, Sauce"
              onKeyPress={(e) => e.key === 'Enter' && handleAddIngredientSection()} />
            <button className="add-button" onClick={handleAddIngredientSection}>Add Section</button>
          </div>
          {recipe.extendedIngredients.length === 0 && (
            <p className="empty-hint">Add a section above to start adding ingredients.</p>
          )}
          {recipe.extendedIngredients.map((section, sIndex) => (
            <div key={sIndex} className="equipment-item" style={{ marginBottom: '1.5rem' }}>
              <div className="equipment-content" style={{ marginBottom: '0.5rem' }}>
                <strong>📂 {section.section}</strong>
                <div className="item-buttons">
                  <button className="edit-button"
                    onClick={() => setActiveIngredientSectionIndex(activeIngredientSectionIndex === sIndex ? null : sIndex)}>
                    {activeIngredientSectionIndex === sIndex ? 'Close' : 'Add Ingredients'}
                  </button>
                  <button className="remove-button" onClick={() => handleRemoveIngredientSection(sIndex)}>Remove Section</button>
                </div>
              </div>
              <ul className="item-list">
                {section.ingredients.map((ingredient, ingIndex) => {
                  const isBeingEdited = editingIngredient?.sectionIndex === sIndex && editingIngredient?.itemIndex === ingIndex;
                  return (
                    <li key={ingIndex} className="item" style={{
                      background: isBeingEdited ? '#fff8e1' : undefined,
                      border: isBeingEdited ? '1px solid #FFD54F' : undefined,
                      borderRadius: isBeingEdited ? '4px' : undefined,
                    }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontWeight: 500 }}>{ingredient.original}</span>
                        {isBeingEdited && (
                          <span style={{ marginLeft: '8px', fontSize: '0.72rem', color: '#E67E22', fontWeight: 600 }}>✏️ editing…</span>
                        )}
                        {!isBeingEdited && ingredient.name && (
                          <span style={{ marginLeft: '8px', fontSize: '0.72rem', color: '#aaa' }}>
                            {[ingredient.quantity != null ? ingredient.quantity : null, ingredient.unit || null, ingredient.name, ingredient.prep || null].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </div>
                      <div className="item-buttons">
                        <button className="edit-button" disabled={isBeingEdited} onClick={() => handleEditIngredient(sIndex, ingIndex)}>
                          {isBeingEdited ? 'Editing…' : 'Edit'}
                        </button>
                        <button className="remove-button" disabled={isBeingEdited} onClick={() => handleRemoveIngredientFromSection(sIndex, ingIndex)}>Remove</button>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {activeIngredientSectionIndex === sIndex && (
                <div id={`ingredient-form-${sIndex}`}>
                  <StructuredIngredientInput
                    onAdd={(fields) => handleAddIngredientToSection(sIndex, fields)}
                    editingValue={editingIngredient?.sectionIndex === sIndex ? editingIngredient.fields : null}
                    isEditing={editingIngredient?.sectionIndex === sIndex}
                    onCancelEdit={() => setEditingIngredient(null)}
                    onSelectCustom={handleSelectCustom}
                    onAddToList={saveIngredient}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            INSTRUCTIONS
        ══════════════════════════════════════════════════════════════════ */}
        <div className="section">
          <h3>Instructions</h3>
          <div style={{ border: '1px dashed #ccc', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
            <h4 style={{ marginBottom: '0.75rem' }}>New Section</h4>
            <div className="form-group">
              <label>Section Name:</label>
              <input type="text" value={currentInstructionSection}
                onChange={(e) => setCurrentInstructionSection(e.target.value)}
                placeholder="e.g., Batter, Topping, Sauce, Filling" />
            </div>
            <div className="form-group">
              <label>Section Note (optional):</label>
              <textarea value={currentSectionNote} onChange={(e) => setCurrentSectionNote(e.target.value)}
                placeholder="e.g., Make this part ahead of time..." rows="2" />
            </div>
            <button className="add-button" onClick={handleAddInstructionSection}>Add Section</button>
          </div>
          {recipe.analyzedInstructions.length === 0 && (
            <p className="empty-hint">Add a section above to start adding steps.</p>
          )}
          {recipe.analyzedInstructions.map((section, sIndex) => (
            <div key={sIndex} className="equipment-item" style={{ marginBottom: '2rem' }}>
              <div className="equipment-content" style={{ marginBottom: '0.75rem' }}>
                <strong>📋 {section.section || `Section ${sIndex + 1}`}</strong>
                <div className="item-buttons">
                  <button className="edit-button"
                    onClick={() => setActiveInstructionSectionIndex(activeInstructionSectionIndex === sIndex ? null : sIndex)}>
                    {activeInstructionSectionIndex === sIndex ? 'Close' : 'Add Steps'}
                  </button>
                  <button className="remove-button" onClick={() => handleRemoveInstructionSection(sIndex)}>Remove Section</button>
                </div>
              </div>
              <div className="form-group">
                <label>Section Note:</label>
                <textarea value={section.note || ""} onChange={(e) => handleUpdateSectionNote(sIndex, e.target.value)}
                  placeholder="Optional note for this section..." rows="2" />
              </div>
              {renderStepItems({ steps: section.steps, sIndex })}
              {activeInstructionSectionIndex === sIndex && (
                <div id={`step-form-${sIndex}`} className="add-item-group" style={{ marginTop: '0.5rem' }}>
                  {editingSectionStep?.sectionIndex === sIndex && (
                    <p style={{ margin: '0 0 6px', fontSize: '0.8rem', color: '#E67E22', fontWeight: 600 }}>
                      {editingSectionStep.isInsert ? `↓ Inserting new step before Step ${editingSectionStep.stepIndex + 1}` : `✏️ Editing Step ${editingSectionStep.stepIndex + 1}`}
                    </p>
                  )}
                  <textarea value={currentSectionStep} onChange={(e) => setCurrentSectionStep(e.target.value)}
                    placeholder="e.g., Mix the flour and sugar together..." rows="3" />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="add-button" onClick={() => handleAddStepToSection(sIndex)}>
                      {editingSectionStep?.sectionIndex === sIndex ? (editingSectionStep.isInsert ? 'Insert Step' : 'Update Step') : 'Add Step'}
                    </button>
                    {editingSectionStep?.sectionIndex === sIndex && (
                      <button className="edit-button" onClick={() => {
                        if (editingSectionStep.originalStep) {
                          const updated = [...recipe.analyzedInstructions];
                          const sec = { ...updated[sIndex], steps: [...updated[sIndex].steps] };
                          sec.steps.splice(editingSectionStep.stepIndex, 0, editingSectionStep.originalStep);
                          sec.steps = sec.steps.map((s, i) => ({ ...s, number: i + 1 }));
                          updated[sIndex] = sec;
                          setRecipeDirty({ ...recipe, analyzedInstructions: updated });
                        }
                        setEditingSectionStep(null); setCurrentSectionStep('');
                      }}>Cancel</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── Equipment ── */}
        <div className="section">
          <h3>Equipment</h3>
          <div className="add-item-group">
            <textarea value={currentEquipmentItems} onChange={(e) => setCurrentEquipmentItems(e.target.value)}
              placeholder="e.g., 7x4 Pan, Mixer with a dough hook attachment" rows="2" />
            <button className="add-button" onClick={handleAddEquipment}>Add Equipment</button>
          </div>
          {recipe.equipment.map((equipment, equipIndex) => (
            <div key={equipIndex} className="equipment-item">
              <div className="equipment-content">
                <strong>Equipment:</strong> {equipment.items}
                <button className="remove-button" onClick={() => handleRemoveEquipment(equipIndex)}>Remove</button>
              </div>
              <div className="image-subsection">
                <h5>Equipment Images:</h5>
                {equipment.images.length > 0 && (
                  <div className="image-list">
                    {equipment.images.map((image, imgIndex) => (
                      <div key={imgIndex} className="image-item">
                        <img src={image.url} alt={image.alt} style={{ width: '60px', height: '45px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
                        <span style={{ flex: 1, fontSize: '0.82rem', color: '#555' }}>{image.alt}</span>
                        <button className="small-remove-button" onClick={() => handleRemoveEquipmentImage(equipIndex, imgIndex)}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="image-input-group" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <ImageUploadButton uid={user?.uid} label={pendingEquipmentImageUrl ? '✓ Uploaded — Replace' : 'Upload Equipment Image'} onUploaded={(url) => setPendingEquipmentImageUrl(url)} />
                    {pendingEquipmentImageUrl && (
                      <img src={pendingEquipmentImageUrl} alt="preview" style={{ width: '50px', height: '38px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }} />
                    )}
                  </div>
                  <input type="text" value={currentEquipmentImageAlt} onChange={(e) => setCurrentEquipmentImageAlt(e.target.value)}
                    placeholder="Image description (alt text)" style={{ width: '100%', boxSizing: 'border-box' }} />
                  <button className="small-button" disabled={!pendingEquipmentImageUrl || !currentEquipmentImageAlt.trim()}
                    style={{ opacity: (!pendingEquipmentImageUrl || !currentEquipmentImageAlt.trim()) ? 0.5 : 1 }}
                    onClick={() => handleAddEquipmentImage(equipIndex)}>Add Image</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Actions ── */}
        <div className="button-group">
          <button className="add-button" onClick={handleSaveToFirebase} disabled={!recipe.id || !recipe.title || saveNotification === 'saving'}
            style={{ background: 'linear-gradient(35deg, #2196F3, #1565C0)', opacity: (!recipe.id || !recipe.title) ? 0.5 : 1 }}>
            {saveNotification === 'saving' ? '⏳ Saving…' : 'Save to Firebase'}
          </button>
          <button className="generate-button" onClick={generateJSON}>Generate JSON</button>
          {recipe.id && (
            <button className="remove-button" style={{ padding: '0.75rem 1.5rem', fontSize: '1rem' }} onClick={handleDeleteFromFirebase}>
              Delete Recipe
            </button>
          )}
        </div>

        {showJSON && (
          <div className="json-section">
            <h3>Recipe JSON</h3>
            <p>Copy this and add it to your <code>recipes.json</code> file:</p>
            <pre className="json-box">{generatedJSON}</pre>
            <div className="button-group">
              <button className="copy-button" onClick={copyToClipboard}>Copy to Clipboard</button>
              <button className="download-button" onClick={downloadJSON}>Download JSON</button>
            </div>
            {customIngredients.length > 0 && (
              <div style={{ marginTop: '2rem', borderTop: '2px solid #F4D03F', paddingTop: '1.5rem' }}>
                <h3>
                  🆕 New Ingredients to Add
                  <span style={{ fontSize: '0.8rem', fontWeight: 400, color: '#888', marginLeft: '10px' }}>
                    {customIngredients.length} custom ingredient{customIngredients.length > 1 ? 's' : ''} used in this recipe
                  </span>
                </h3>
                <p style={{ color: '#666', fontSize: '0.9rem' }}>Set a category and optional aliases for each new ingredient, then save them to Firebase.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '1rem' }}>
                  {customIngredients.map((ci, i) => (
                    <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', background: '#fffde7', border: '1px solid #F4D03F', borderRadius: '8px', padding: '10px 12px' }}>
                      <span style={{ fontWeight: 600, flex: '1 1 140px' }}>{ci.name}</span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: '1 1 200px' }}>
                        <label style={{ fontSize: '0.72rem', color: '#888', fontWeight: 600 }}>Category</label>
                        <select value={ci.category} onChange={(e) => updateCustomIngredient(i, 'category', e.target.value)} style={{ width: '100%' }}>
                          {CATEGORY_ORDER.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: '2 1 200px' }}>
                        <label style={{ fontSize: '0.72rem', color: '#888', fontWeight: 600 }}>Aliases (optional, comma-separated)</label>
                        <input type="text" value={ci.aliases} onChange={(e) => updateCustomIngredient(i, 'aliases', e.target.value)}
                          placeholder="e.g. scallions, spring onions" style={{ width: '100%', boxSizing: 'border-box' }} />
                      </div>
                      <button className="remove-button" onClick={() => removeCustomIngredient(i)} title="Remove from list" style={{ flexShrink: 0 }}>✕</button>
                    </div>
                  ))}
                </div>
                <div className="button-group" style={{ marginTop: '0.5rem' }}>
                  <button className="add-button" style={{ background: 'linear-gradient(35deg, #2196F3, #1565C0)' }}
                    onClick={async () => {
                      let saved = 0;
                      for (const ci of customIngredients) {
                        try {
                          await saveIngredient({ name: ci.name, category: ci.category, aliases: ci.aliases ? ci.aliases.split(',').map(a => a.trim()).filter(Boolean) : [] });
                          saved++;
                        } catch (err) { console.error('Failed to save', ci.name, err); }
                      }
                      setCustomIngredients([]);
                      alert(saved + ' ingredient' + (saved !== 1 ? 's' : '') + ' saved to Firebase!');
                    }}>
                    Save All to Firebase
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default AddRecipe;
